import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";

/**
 * Renders an image with a spinner overlay shown until it finishes loading.
 * Mirrors the concept of ImageWithLoader in ngwaahia-mobile/components/ui/ImageWithLoader.js.
 *
 * Props:
 *   src        string         image URL
 *   alt        string         alt text (default "")
 *   objectFit  string         CSS object-fit for the <img> (default "cover")
 *   sx         object         MUI sx for the outer container — set width/height here
 *   imgStyle   object         extra inline styles merged onto the <img>
 */
const ImageWithLoader = ({
  src,
  alt = "",
  objectFit = "cover",
  sx,
  imgStyle,
  ...imgProps
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  // Reset so a src change (e.g. after a photo upload) gets a fresh attempt
  // instead of being stuck showing the previous src's error state. Also
  // checks img.complete: a cached image can finish loading (or already be
  // complete on mount) before this onLoad handler is attached, so the
  // browser never fires another `load` event — without this check isLoading
  // gets stuck true forever, showing the spinner over an already-rendered
  // image.
  useEffect(() => {
    setHasError(false);
    setIsLoading(!imgRef.current?.complete);
  }, [src]);

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        width: "100%",
        height: "100%",
        bgcolor: hasError ? "#e5e7eb" : undefined,
        ...sx,
      }}
    >
      {/* Unmounted (not just hidden) on error — a failed <img> otherwise falls
          back to rendering its alt text, which can overflow this box since
          browsers don't clip that fallback content to the element's size. */}
      {!hasError && (
        <img
          ref={imgRef}
          src={src}
          alt={alt}
          style={{
            width: "100%",
            height: "100%",
            objectFit,
            display: "block",
            ...imgStyle,
          }}
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false);
            setHasError(true);
          }}
          {...imgProps}
        />
      )}
      {isLoading && !hasError && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0,0,0,0.05)",
          }}
        >
          <CircularProgress size={24} sx={{ color: "#046287" }} />
        </Box>
      )}
    </Box>
  );
};

export default ImageWithLoader;
